import React, { useCallback } from 'react'
import { UserService } from '@duck/api-definitions'
import { AsyncMultiPick, type AsyncMultiPickProps, type MultiPickOption } from '@duck/ui'
import { apis } from '@/modules/apiClients'

export interface UserDataInputProps extends Omit<AsyncMultiPickProps, 'mode' | 'fetchOptions'> {
  /** Max number of results to return from the backend. Default: 20. */
  maxResults?: number
}

/**
 * Shared user search -> options mapper, reused by both the generic data input
 * and table filters that need async user search.
 */
export const fetchUserOptions = async (
  query?: string,
  maxResults = 20,
): Promise<MultiPickOption[]> => {
  const trimmed = query?.trim()
  if (!trimmed) return []

  const { data } = await apis.userService.searchGet(trimmed, maxResults)

  return data.map((u: UserService.UserSearchResult) => ({
    value: u.id,
    label: `${u.firstName} ${u.lastName} (${u.email})`,
  }))
}

export const UserDataInput: React.FC<UserDataInputProps> = ({
  maxResults = 20,
  ...props
}) => {
  const fetchOptions = useCallback(
    (query?: string) => fetchUserOptions(query, maxResults),
    [maxResults],
  )

  return (
    <AsyncMultiPick
      mode="search"
      fetchOptions={fetchOptions}
      {...props}
    />
  )
}

